// frontend/src/App.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import RouteScrollToTop from './components/common/RouteScrollToTop';
import Preloader from './components/common/Preloader';
import Banner from './components/common/Banner';
import CompareFloatingBar from './components/common/CompareFloatingBar';
import MobileBottomNav from './components/layout/MobileBottomNav';

import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ServicesPage from './pages/ServicesPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import UserProfilePage from './pages/UserProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PartsPage from './pages/PartsPage';
import PartDetailPage from './pages/PartDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import OrderHistoryPage from './pages/OrderHistoryPage';
import SellCarPage from './pages/SellCarPage';
import VehicleMarketplacePage from './pages/VehicleMarketplacePage';
import VehicleDetailPage from './pages/VehicleDetailPage';
import EditVehiclePage from './pages/EditVehiclePage';
import ServiceBookingPage from './pages/ServiceBookingPage';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailedPage from './pages/PaymentFailedPage';
import TicketDetailPage from './pages/TicketDetailPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage'; 
import PartnerTermsPage from './pages/PartnerTermsPage'; 
import GarageIntroPage from './pages/GarageIntroPage'; 
import ComparePage from './pages/ComparePage';
import StorefrontPage from './pages/StorefrontPage'; 

// --- ADMIN IMPORTS ---
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminServiceBookings from './pages/admin/AdminServiceBookings';
import AdminOrders from './pages/admin/AdminOrders';
import AdminUsers from './pages/admin/AdminUsers';
import AdminInventory from './pages/admin/AdminInventory';
import AdminVehicles from './pages/admin/AdminVehicles';
import AdminZones from './pages/admin/AdminZones';
import AdminStores from './pages/admin/AdminStores';
import AdminProviders from './pages/admin/AdminProviders';
import AdminDelivery from './pages/admin/AdminDelivery';
import AdminSettings from './pages/admin/AdminSettings'; 
import AdminSupport from './pages/admin/AdminSupport';
import AdminTicketDetail from './pages/admin/AdminTicketDetail';
import AdminBulkRequests from './pages/admin/AdminBulkRequests';
import AdminBulkDetail from './pages/admin/AdminBulkDetail';
import AdminInspectionForm from './pages/admin/AdminInspectionForm';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminShipping from './pages/admin/AdminShipping';
import AdminAILogs from './pages/admin/AdminAILogs';
import AdminCatalog from './pages/admin/AdminCatalog';
import AdminFinance from './pages/admin/AdminFinance';
import AdminContent from './pages/admin/AdminContent';
import AdminInventoryReports from './pages/admin/AdminInventoryReports';
import AdminAIAnalyst from './pages/admin/AdminAIAnalyst';
import AdminReviews from './pages/admin/AdminReviews';
import AdminRetention from './pages/admin/AdminRetention';
import AdminPayouts from './pages/admin/AdminPayouts';
import AdminEditVehiclePage from './pages/admin/AdminEditVehiclePage';
import AdminEAHub from './pages/admin/AdminEAHub';
import AdminAISystemSettings from './pages/admin/AdminAISystemSettings';
import AdminResetPage from './pages/admin/AdminResetPage'; 
import AdminNotificationSettings from './pages/admin/AdminNotificationSettings'; // <--- NEW IMPORT
import AdminFundWallet from './pages/admin/AdminFundWallet';
import VerificationPage from './pages/VerificationPage';

import AIChatInterface from './components/ai/AIChatInterface';
import AIChatBubble from './components/ai/AIChatBubble';

import './App.css';

import { useAuth } from './context/AuthContext';
import { useCart } from './context/CartContext';
import { SavedProvider, useSaved } from './context/SavedContext';
import { CompareProvider } from './context/CompareContext';
import aiService from './services/ai';
import { settingsApi } from './api/settings'; 

// --- ROUTE GUARDS ---

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function ProtectedAdminRoute({ children }) {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  const allowedRoles = ['super_admin', 'admin', 'store_manager', 'logistics_manager', 'territory_coordinator'];
  const hasAccess = user?.roles?.some(role => allowedRoles.includes(role));
  
  if (!hasAccess) return <Navigate to="/" replace />;
  
  return children;
}

const CHAT_STATE_STORAGE_KEY = 'aiChatState';
const CHAT_SESSION_STORAGE_KEY = 'aiChatSessionId';

function AppContent() {
  const { isAuthenticated, logout } = useAuth();
  const { refreshCart } = useCart();
  const { refreshSaved } = useSaved();
  const location = useLocation(); 
  
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatSending, setIsChatSending] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [chatPosition, setChatPosition] = useState(() => JSON.parse(localStorage.getItem(CHAT_STATE_STORAGE_KEY))?.position || { x: 0, y: 0 });
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(CHAT_SESSION_STORAGE_KEY) || uuidv4());
  const isStreamingAssistantMessage = useRef(false);
  const fullResponseRef = useRef("");
  
  const [visionEnabled, setVisionEnabled] = useState(true);

  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => { localStorage.setItem(CHAT_SESSION_STORAGE_KEY, sessionId); }, [sessionId]);
  useEffect(() => { localStorage.setItem(CHAT_STATE_STORAGE_KEY, JSON.stringify({ isOpen: isChatOpen, position: chatPosition })); }, [isChatOpen, chatPosition]);

  useEffect(() => {
      const fetchSettings = async () => {
          try {
              const settings = await settingsApi.getAllSettings();
              const isEnabled = settings.ai_config?.vision_enabled !== false;
              setVisionEnabled(isEnabled);
          } catch (e) {
              console.error("Failed to fetch AI settings", e);
          }
      };
      fetchSettings();
  }, []);

  const closeChat = useCallback(() => { setIsChatOpen(false); setChatError(null); setIsChatSending(false); isStreamingAssistantMessage.current = false; }, []);
  const handleClearChat = useCallback(() => { const newId = uuidv4(); setSessionId(newId); localStorage.setItem(CHAT_SESSION_STORAGE_KEY, newId); setChatMessages([{ sender: 'assistant', text: "Conversation cleared." }]); setChatError(null); setIsChatSending(false); }, []);

  const openChat = useCallback(async () => {
    setIsChatOpen(true);
    setChatError(null);
    if (chatMessages.length > 0) { setIsChatSending(false); isStreamingAssistantMessage.current = false; return; }
    if (isAuthenticated) {
      setIsChatSending(true);
      const result = await aiService.getChatHistory(sessionId);
      setChatMessages(result.success && result.history.length > 0 ? result.history : [{ sender: 'assistant', text: "Hello! How can I help?" }]);
      if (!result.success) setChatError(result.error);
      setIsChatSending(false);
    } else {
      setChatMessages([{ sender: 'assistant', text: "Hello! Please log in to chat." }]);
    }
  }, [isAuthenticated, sessionId, chatMessages.length]);

  const handleChunkReceived = useCallback((chunk) => {
    if (chunk) fullResponseRef.current += chunk;
    setChatMessages(prev => {
      if (!isStreamingAssistantMessage.current) {
        isStreamingAssistantMessage.current = true;
        return [...prev, { sender: 'assistant', text: chunk }];
      }
      const lastMsg = prev[prev.length - 1];
      if (lastMsg?.sender === 'assistant') {
        lastMsg.text += chunk;
        return [...prev];
      }
      return [...prev, { sender: 'assistant', text: chunk }];
    });
  }, []);

  const handleStreamEnd = useCallback(() => {
    isStreamingAssistantMessage.current = false;
    setIsChatSending(false);
    const responseText = fullResponseRef.current.toLowerCase();
    if (responseText.includes("added") && responseText.includes("cart")) refreshCart();
    if (responseText.includes("saved") || responseText.includes("wishlist")) refreshSaved();
    fullResponseRef.current = "";
  }, [refreshCart, refreshSaved]);

  const handleStreamError = useCallback(async (errorMsg) => {
    setChatError(errorMsg);
    isStreamingAssistantMessage.current = false;
    setIsChatSending(false);
    fullResponseRef.current = "";
    if (errorMsg.includes("401") || errorMsg.includes("token")) {
      await logout();
      setChatMessages([]);
      closeChat();
    }
  }, [logout, closeChat]);

  const sendChatMessage = useCallback(async (message, imageUrl = null) => {
    if (!isAuthenticated || isChatSending) return;
    
    let userMessageText = message;
    if (imageUrl) {
        userMessageText += `\n\n![User Upload](${imageUrl})`;
    }
    setChatMessages(prev => [...prev, { sender: 'user', text: userMessageText }]);
    
    setIsChatSending(true);
    setChatError(null);
    isStreamingAssistantMessage.current = false;
    fullResponseRef.current = "";
    
    await aiService.sendChatMessageStream(sessionId, message, imageUrl, handleChunkReceived, handleStreamEnd, handleStreamError);
  }, [isAuthenticated, isChatSending, sessionId, handleChunkReceived, handleStreamEnd, handleStreamError]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Preloader />
      <Banner />
      
      {!isAdminRoute && <Navbar onCloseChat={closeChat} />}
      
      <main className="flex-grow w-full">
        <RouteScrollToTop />
        <Routes>
          <Route path="/*" element={<PublicRoutes onOpenAIChat={openChat} />} />
          <Route path="/admin/*" element={<AdminRoutes />} />
        </Routes>
      </main>
      
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <MobileBottomNav />}
      
      <CompareFloatingBar />
      <ScrollToTop />
      
      {isChatOpen && (
        <AIChatInterface 
            messages={chatMessages} 
            onSendMessage={sendChatMessage} 
            isSending={isChatSending} 
            error={chatError} 
            setChatError={setChatError} 
            onClose={closeChat} 
            onClearChat={handleClearChat} 
            position={chatPosition} 
            setPosition={setChatPosition} 
            visionEnabled={visionEnabled} 
        />
      )}
      {!isChatOpen && <AIChatBubble onOpenChat={openChat} />}
    </div>
  );
}

const PublicRoutes = ({ onOpenAIChat }) => (
  <Routes>
    <Route path="/" element={<HomePage onOpenAIChat={onOpenAIChat} />} />
    <Route path="/about" element={<AboutPage />} />
    <Route path="/contact" element={<ContactPage />} />
    <Route path="/services" element={<ServicesPage />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/verification" element={<VerificationPage />} />
    <Route path="/parts" element={<PartsPage />} />
    <Route path="/parts/categories" element={<PartsPage />} />
    <Route path="/parts/:id" element={<PartDetailPage />} />
    <Route path="/cart" element={<CartPage />} />
    <Route path="/vehicles/browse" element={<VehicleMarketplacePage />} />
    <Route path="/vehicles/:id" element={<VehicleDetailPage />} />
    <Route path="/store/:storeId" element={<StorefrontPage />} /> 
    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
    <Route path="/terms" element={<TermsPage />} /> 
    <Route path="/partner-terms" element={<PartnerTermsPage />} /> 
    <Route path="/garage-benefits" element={<GarageIntroPage />} /> 
    <Route path="/compare" element={<ComparePage />} />
    
    <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
    <Route path="/order-success" element={<ProtectedRoute><OrderSuccessPage /></ProtectedRoute>} />
    <Route path="/account" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
    <Route path="/account/orders" element={<ProtectedRoute><OrderHistoryPage /></ProtectedRoute>} />
    <Route path="/account/support/:ticketId" element={<ProtectedRoute><TicketDetailPage /></ProtectedRoute>} />
    <Route path="/vehicles/sell" element={<ProtectedRoute><SellCarPage /></ProtectedRoute>} />
    <Route path="/vehicles/edit/:id" element={<ProtectedRoute><EditVehiclePage /></ProtectedRoute>} />
    <Route path="/services/schedule" element={<ProtectedRoute><ServiceBookingPage /></ProtectedRoute>} />
    
    <Route path="/pay/:invoiceId" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
    <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
    <Route path="/payment-failed" element={<ProtectedRoute><PaymentFailedPage /></ProtectedRoute>} />
  </Routes>
);

const AdminRoutes = () => (
  <Routes>
    <Route path="/" element={<ProtectedAdminRoute><AdminLayout /></ProtectedAdminRoute>}>
      <Route index element={<AdminDashboard />} />
      <Route path="ea-hub" element={<AdminEAHub />} />
      <Route path="services" element={<AdminServiceBookings />} />
      <Route path="orders" element={<AdminOrders />} />
      <Route path="delivery" element={<AdminDelivery />} />
      <Route path="users" element={<AdminUsers />} />
      <Route path="inventory" element={<AdminInventory />} />
      <Route path="catalog" element={<AdminCatalog />} />
      <Route path="vehicles" element={<AdminVehicles />} />
      <Route path="vehicles/edit/:id" element={<AdminEditVehiclePage />} />
      <Route path="zones" element={<AdminZones />} />
      <Route path="stores" element={<AdminStores />} />
      <Route path="providers" element={<AdminProviders />} />
      <Route path="settings" element={<AdminSettings />} /> 
      <Route path="notifications" element={<AdminNotificationSettings />} /> {/* <--- NEW ROUTE */}
      <Route path="ai-system" element={<AdminAISystemSettings />} />
      <Route path="support" element={<AdminSupport />} />
      <Route path="support/:ticketId" element={<AdminTicketDetail />} />
      <Route path="bulk" element={<AdminBulkRequests />} />
      <Route path="bulk/:requestId" element={<AdminBulkDetail />} />
      <Route path="inspections/new/:vehicleId" element={<AdminInspectionForm />} />
      <Route path="analytics" element={<AdminAnalytics />} />
      <Route path="shipping" element={<AdminShipping />} />
      <Route path="ai-logs" element={<AdminAILogs />} />
      <Route path="finance" element={<AdminFinance />} />
      <Route path="content" element={<AdminContent />} />
      <Route path="reports" element={<AdminInventoryReports />} />
      <Route path="executive-analyst" element={<AdminAIAnalyst />} />
      <Route path="reviews" element={<AdminReviews />} />
      <Route path="retention" element={<AdminRetention />} />
      <Route path="payouts" element={<AdminPayouts />} />
      <Route path="fund-wallet" element={<AdminFundWallet />} />
      <Route path="reset" element={<AdminResetPage />} /> 
    </Route>
  </Routes>
);

function App() {
  return (
    <Router>
      <CompareProvider>
        <SavedProvider>
          <AppContent />
        </SavedProvider>
      </CompareProvider>
    </Router>
  );
}

export default App;